import { Phone, MessageCircle, CalendarDays, ClipboardList, GraduationCap } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { EnquiryForm } from "../components/EnquiryForm";
import { Placeholder } from "../components/Placeholder";
import { site, admissionSteps } from "../config/siteConfig";

const infoCards = [
  { icon: GraduationCap, label: "Available Classes", value: site.classes },
  { icon: ClipboardList, label: "Admission Requirements", value: site.documents },
  { icon: CalendarDays, label: "Important Dates", value: site.admissionDates },
];

export default function AdmissionsPage() {
  return (
    <>
      <Seo
        title="Admissions | Ansar Public School Mundeke Goraya, Daska"
        description="Start an admission enquiry for Ansar Public School Mundeke Goraya, Daska. Learn about the admission process and contact the school team."
      />
      <PageHero
        chapter="01"
        eyebrow="Admissions"
        title="Start Your Child's Admission Journey"
        description="Make an enquiry, confirm the available class and complete the school's admission process — with guidance from our team at every step."
        crumb="Admissions"
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="grid gap-4 md:grid-cols-3">
            {infoCards.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`admission-info-${i}`}
                  className="flex h-full flex-col gap-3 rounded-3xl border border-brand-line bg-brand-cream p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-light">
                    <c.icon className="h-5 w-5 text-brand" aria-hidden="true" />
                  </span>
                  <h2 className="font-display text-base font-bold text-brand-ink">{c.label}</h2>
                  <Placeholder>{c.value}</Placeholder>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-4 text-sm text-brand-muted">
              Fee information is shared directly by the school office —{" "}
              <Placeholder className="align-middle">{site.fees}</Placeholder>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionHeading
                chapter="02"
                eyebrow="The Process"
                title="How Admission Works"
                description="Four simple steps — our team stays in touch with you throughout."
              />
            </Reveal>
            <div className="mt-10 space-y-4">
              {admissionSteps.map((s, i) => (
                <Reveal key={s.step} delay={i * 0.08}>
                  <article
                    data-testid={`admission-step-${i}`}
                    className="flex gap-5 rounded-3xl border border-brand-line bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand font-display text-base font-extrabold text-white">
                      {s.step}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-brand-ink">{s.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-brand-muted">{s.text}</p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <a
                  href={site.phoneHref}
                  data-testid="admissions-call-cta"
                  className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-brand px-5 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call — {site.phoneDisplay}
                </a>
                <a
                  href={site.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admissions-whatsapp-cta"
                  className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border border-brand-green bg-white px-5 py-3 text-[15px] font-semibold text-brand-green transition-colors hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                  WhatsApp the School
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div
              data-testid="admissions-form-card"
              className="rounded-[2rem] border border-brand-line bg-white p-6 shadow-xl sm:p-9 lg:sticky lg:top-24"
            >
              <h2 className="font-display text-2xl font-bold text-brand-ink">
                Admission Enquiry Form
              </h2>
              <p className="mb-7 mt-2 text-sm leading-relaxed text-brand-muted">
                Fill in the details below and the school team will contact you. No
                payment or documents are needed at this stage.
              </p>
              <EnquiryForm source="admissions" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
